import React from 'react';

import { formatEntryDateTime } from '~/components/ClaimPage/CliveApp/ConductorHub/utils';
import type { NumberOfLines } from '~/components/core/Molecules/Collapsible/ShowAllCollapsible';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import cn from '~/Utils/cn';

import type { AccentVariant } from './accents';
import { ACCENT_CONFIG } from './accents';
import NotificationMessage from './NotificationMessage';
import type { StpNotification } from './types';

interface AccentCardProps {
  notification: StpNotification;
  accent: AccentVariant;
  /** Clamp the message to this many lines behind Show more. */
  visibleLines?: NumberOfLines;
}

/**
 * The body of an accented entry: the variant's label and the entry's time over its title and
 * message, in the variant's frame.
 *
 * The card only — the rail node and the row frame belong to the conductor log drawing it.
 */
const AccentCard: React.FC<AccentCardProps> = ({ notification, accent, visibleLines }) => {
  const { title, message, timestamp } = notification;
  const config = ACCENT_CONFIG[accent];
  const time = formatEntryDateTime(timestamp);

  return (
    <div className={cn('rounded-lg border border-solid px-16 py-12', config.cardClassName)}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-8">
        <Text
          variant={Text.VARIANTS.XS}
          weight={Text.WEIGHTS.SEMI_BOLD}
          className={cn('uppercase tracking-wider', config.labelClassName)}
        >
          {config.label}
        </Text>
        {time ? (
          <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="whitespace-nowrap">
            {time}
          </Text>
        ) : null}
      </div>
      {title ? <Heading variant={Heading.TYPES.H4}>{title}</Heading> : null}
      {message ? <NotificationMessage message={message} className="mt-2" visibleLines={visibleLines} /> : null}
    </div>
  );
};

export default AccentCard;
